/**
 * EYVA Blog – carousel, category filter, and load-more interactions
 */
class EyvaBlog {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.initCarousel();
    this.initCategoryFilter();
    this.initLoadMore();
    this.initRecentLoadMore();
  }

  /* ── Hero carousel ── */
  initCarousel() {
    const track = this.root.querySelector('[data-carousel-track]');
    const dots = this.root.querySelectorAll('[data-carousel-dot]');
    if (!track || dots.length === 0) return;

    let currentIndex = 0;
    let touchStartX = 0;
    let isMouseDown = false;
    const swipeThreshold = 50;

    const scrollToSlide = (index) => {
      const slides = track.querySelectorAll('[data-carousel-slide]');
      if (slides.length === 0) return;

      currentIndex = ((index % slides.length) + slides.length) % slides.length;
      const target = slides[currentIndex];
      const scrollPosition =
        target.offsetLeft - (track.offsetWidth - target.offsetWidth) / 2;

      track.scrollTo({ left: scrollPosition, behavior: 'smooth' });

      dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === currentIndex);
      });
    };

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => scrollToSlide(i));
    });

    const handleSwipe = (endX) => {
      const distance = endX - touchStartX;
      if (Math.abs(distance) > swipeThreshold) {
        scrollToSlide(currentIndex + (distance < 0 ? 1 : -1));
      } else {
        scrollToSlide(currentIndex);
      }
    };

    track.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    track.addEventListener('touchend', (e) => {
      handleSwipe(e.changedTouches[0].screenX);
    }, { passive: true });

    track.addEventListener('mousedown', (e) => {
      touchStartX = e.screenX;
      isMouseDown = true;
      e.preventDefault();
    });

    track.addEventListener('mouseup', (e) => {
      if (!isMouseDown) return;
      handleSwipe(e.screenX);
      isMouseDown = false;
    });

    track.addEventListener('mouseleave', () => {
      isMouseDown = false;
    });
  }

  /* ── Category filter ── */
  initCategoryFilter() {
    const filterBtns = this.root.querySelectorAll('[data-filter-btn]');
    const cards = this.root.querySelectorAll('[data-featured-card]');
    const noBlogs = this.root.querySelector('[data-no-blogs]');
    const loadMoreBtn = this.root.querySelector('[data-featured-load-more]');

    if (filterBtns.length === 0) return;

    let activeCategory = 'All Categories';
    let isExpanded = false;
    const initialCount = parseInt(this.root.dataset.featuredInitial || '6', 10);

    const getVisibleCards = () => {
      return Array.from(cards).filter((card) => {
        if (activeCategory === 'All Categories') return true;
        const tags = (card.dataset.tags || '').split(',').map((t) => t.trim());
        return tags.includes(activeCategory);
      });
    };

    const renderFeatured = () => {
      const visible = getVisibleCards();

      if (noBlogs) {
        noBlogs.classList.toggle('visible', visible.length === 0);
      }

      cards.forEach((card) => card.classList.add('hidden'));

      const toShow = isExpanded ? visible : visible.slice(0, initialCount);
      toShow.forEach((card) => card.classList.remove('hidden'));

      if (loadMoreBtn) {
        const showBtn =
          activeCategory === 'All Categories' || visible.length > initialCount;
        loadMoreBtn.closest('.eyva-btn-wrap').style.display = showBtn
          ? ''
          : 'none';
        loadMoreBtn.textContent = isExpanded ? 'Show Less' : 'Load More';
      }
    };

    filterBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        filterBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        activeCategory = btn.dataset.category;
        isExpanded = false;
        renderFeatured();
      });
    });

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', () => {
        isExpanded = !isExpanded;
        renderFeatured();
      });
    }

    renderFeatured();
  }

  /* ── Recent blogs load more ── */
  initRecentLoadMore() {
    const loadMoreBtn = this.root.querySelector('[data-recent-load-more]');
    const cards = this.root.querySelectorAll('[data-recent-card]');
    if (!loadMoreBtn || cards.length === 0) return;

    const initialCount = parseInt(this.root.dataset.recentInitial || '3', 10);
    let isExpanded = false;

    const renderRecent = () => {
      cards.forEach((card, i) => {
        card.classList.toggle('hidden', !isExpanded && i >= initialCount);
      });
      loadMoreBtn.textContent = isExpanded ? 'Show Less' : 'Load More';
    };

    loadMoreBtn.addEventListener('click', () => {
      isExpanded = !isExpanded;
      renderRecent();
    });

    renderRecent();
  }
}

class EyvaBlogArticle {
  constructor(root) {
    this.root = root;
    this.initRelatedLoadMore();
  }

  initRelatedLoadMore() {
    const loadMoreBtn = this.root.querySelector('[data-related-load-more]');
    const cards = this.root.querySelectorAll('[data-related-card]');
    if (!loadMoreBtn || cards.length === 0) return;

    const initialCount = parseInt(this.root.dataset.relatedInitial || '3', 10);
    let isExpanded = false;

    const render = () => {
      cards.forEach((card, i) => {
        card.classList.toggle('hidden', !isExpanded && i >= initialCount);
      });
      loadMoreBtn.textContent = isExpanded ? 'Show Less' : 'Load More';
    };

    loadMoreBtn.addEventListener('click', () => {
      isExpanded = !isExpanded;
      render();
    });

    render();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-eyva-blog]').forEach((el) => {
    new EyvaBlog(el);
  });

  document.querySelectorAll('[data-eyva-blog-article]').forEach((el) => {
    new EyvaBlogArticle(el);
  });
});

if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const blogRoot = event.target.querySelector('[data-eyva-blog]');
    if (blogRoot) new EyvaBlog(blogRoot);

    const articleRoot = event.target.querySelector('[data-eyva-blog-article]');
    if (articleRoot) new EyvaBlogArticle(articleRoot);
  });
}
